import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, toRefs, watch, nextTick } from '@vue/composition-api';
import { ContentType, ContentFile } from '@/shared/type';
import CommonCategory from '@/components/common/common-content-category.component.vue';         // カテゴリ選択共通コンポーネント
import vueFilePond from 'vue-filepond';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import { FilePondFile, FileOrigin } from 'filepond';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                    // Windowサイズ
    isAddMode: boolean;                                      // true: 登録処理, false: 編集処理
    content: ContentType;                                    // 教育用コンテンツフォーム
    validateValues: ValidateType;
    isToolbarBr: boolean;                                    // リッチテキストのツールバーの改行フラグ
    isBodyValid: boolean;                                    // 掲載内容のバリデーション結果
    isReferenceUrlValid: boolean;                            // 参考URLのバリデーション結果
    isFileValid: boolean;                                    // 添付ファイルのバリデーション結果
    bodyErrorMessage: string;                                // 掲載内容のバリデーションメッセージ文字列
    referenceUrlErrorMessage: string;                        // 参考URLのバリデーションメッセージ文字列
    fileErrorMessage: string;                                // 添付ファイルのバリデーションメッセージ文字列
    categoryId: string;                                      // カテゴリ選択で選択されたカテゴリID
    isDarkTheme: boolean;                                    // ダークテーマ使用フラグ
    bodyEditorKey: number;                                   // リッチテキストエディターのキー（更新用）
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                              // パラメーター名
    value: string;                                                      // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 教育用コンテンツ登録/編集画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, FilePond, CommonCategory },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.CONTENT;                                 // フォーム高さ調整用変数
        const form = ref<any>(null);                                                        // 入力フォーム
        const categoryRef = ref<any>(null);                                                 // カテゴリー選択
        const router = vueRouter;                                                           // ルータ
        const pondRef = ref<any>(null);                                                     // FilePondへの参照
        const commonRef = ref<any>(null);                                                   // メッセージダイアログ
        const createStyleSet = {
            // 作成形式（選択）の値
            simple: SystemConst.CONTENT_CREATE_METHOD.SIMPLE,                               // 簡易登録
            link: SystemConst.CONTENT_CREATE_METHOD.LINK                                    // リンクURL登録
        };
        const publishRange = {
            // 公開範囲（選択）の値
            forTeacher: SystemConst.CONTENT_PUBLICATION_RANGE.TEACHER,                      // 教職員のみへ公開
            forTeacherAndStudent: SystemConst.CONTENT_PUBLICATION_RANGE.TEACHER_STUDENT     // 教職員及び生徒へ公開
        };

        // 編集モード時の一時保存用変数
        let backupLinkUrl = '';                                                             // リンクURL
        let backupReferenceUrl = '';                                                        // 参考URL
        let backupBody = '';                                                                // 掲載内容
        let backupBodyText = '';                                                            // 掲載内容（テキスト）
        let backupFiles: ContentFile[] = [];                                                // 添付ファイル

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                                     // ウィンドウサイズ
            isAddMode: true,                                                                // true: 登録処理, false: 編集処理
            content: {                                                                      // 教育用コンテンツフォーム
                title: '',
                publishRange: publishRange['forTeacher'],
                createMethod: createStyleSet['simple'],
                linkUrl: '',
                body: '',
                bodyText: '',
                referenceUrl: '',
                files: []
            },
            validateValues: {},
            isToolbarBr: false,                                                             // リッチテキストのツールバーの改行フラグ
            isBodyValid: true,                                                              // 掲載内容のバリデーション結果
            isReferenceUrlValid: true,                                                      // 参考URLのバリデーション結果
            isFileValid: true,                                                              // 添付ファイルのバリデーション結果
            bodyErrorMessage: '',                                                           // 掲載内容のバリデーションメッセージ文字列
            referenceUrlErrorMessage: '',                                                   // 参考URLのバリデーションメッセージ文字列
            fileErrorMessage: '',                                                           // 添付ファイルのバリデーションメッセージ文字列
            categoryId: '1',                                                                // カテゴリ選択で選択されたカテゴリID
            isDarkTheme: ctx.root.$vuetify.theme.dark,                                      // ダークテーマ使用フラグ
            bodyEditorKey: 0                                                                // リッチテキストエディターのキー（更新用）
        });

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(ctx.root.$store.state.commonLayout, () => {
            state.isDarkTheme = ctx.root.$store.state.commonLayout.isDarkTheme;
        })

        // バリデーション用最大桁数
        const maxLength = {
            // タイトル
            title: SystemConst.CONTENT_MAX_LENGTH.TITLE,
            // 掲載内容
            body: SystemConst.CONTENT_MAX_LENGTH.BODY,
            // 参考URL
            referenceUrl: SystemConst.CONTENT_MAX_LENGTH.REFERENCE_URL,
            // リンクURL
            linkUrl: SystemConst.CONTENT_MAX_LENGTH.LINK_URL
        };

        // タイトルのバリデーション
        const titleRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['title'], 'タイトル')
        ];

        // 掲載内容のバリデーション
        const bodyRules = [
            () => {
                const files: FilePondFile[] = pondRef.value.getFiles();

                let hasUrl = false;
                if (typeof state.content.referenceUrl === 'string') {
                    // 参考URLを改行コードで分ける
                    const parts = String(state.content.referenceUrl).split('\n');
    
                    for (const key in parts) {
                        // 参考URLが記述されているか
                        if (parts[key].length !== 0) {
                            // 参考URLが記述されている場合フラグをtrueにする
                            hasUrl = true;
                            break;
                        }
                    }
                }

                // 必須チェック            
                if (files.length === 0 && state.content.bodyText.length === 0 && !hasUrl) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_THREE_OF_ONE, ['掲載内容', '参考URL', '添付ファイル']);
                }
                // 桁数チェック
                if (state.content.bodyText.length > maxLength['body']) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['掲載内容', maxLength['body']]);
                }

                return true;
            }
        ];

        // 参考URLのバリデーション
        const referenceUrlRules = [
            () => {
                const files: FilePondFile[] = pondRef.value.getFiles();

                let hasUrl = false;
                let parts: string[] = [''];
                if (typeof state.content.referenceUrl === 'string') {
                    // 参考URLを改行コードで分ける
                    parts = String(state.content.referenceUrl).split('\n');
    
                    for (const key in parts) {
                        // 参考URLが記述されているか
                        if (parts[key].length !== 0) {
                            // 参考URLが記述されている場合フラグをtrueにする
                            hasUrl = true;
                            break;
                        }
                    }
                }

                // 必須チェック            
                if (files.length === 0 && state.content.bodyText.length === 0 && !hasUrl) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_THREE_OF_ONE, ['掲載内容', '参考URL', '添付ファイル']);
                }
                if (parts[0] !== '') {
                    // 桁数チェック
                    if (state.content.referenceUrl.length > maxLength['referenceUrl']) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_MAX, ['参考URL', maxLength['referenceUrl']]);
                    }
                    // 属性チェック（http:// か https:// から始まる）
                    for (const key in parts) {
                        // 長さ0以上の各文字列に対してチェックする
                        if (parts[key].length !== 0 &&
                            !parts[key].startsWith(SystemConst.URL_SCHEME['HTTP']) &&
                            !parts[key].startsWith(SystemConst.URL_SCHEME['HTTPS'])) {
                            // URLはhttp://またはhttps://から始まる文字列で入力してください。
                            return CommonFunction.replaceArray(
                                MessageConst.E_URL_SCHEME_ERR, [SystemConst.URL_SCHEME['HTTP'], SystemConst.URL_SCHEME['HTTPS']])
                        }
                    }
                    // 重複チェック（参考URLが重複）
                    // 改行のみでない場合重複チェックを行う
                    if (hasUrl) {
                        if (Array.from(new Set(parts.map(e => e))).length !== parts.length) {
                            // 重複を排除した配列の長さが異なれば、重複あり
                            return MessageConst.E_URL_DUPLICATE;
                        }
                    }
                }

                return true;
            }
        ];

        // 添付ファイルのバリデーション
        const filesRules = [
            () => {
                // バリデーションはAPIの結果(m_parametersテーブル)を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // 添付ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = pondRef.value.getFiles();

                // バリデーション値
                const maxCount = Number(state.validateValues['fileCount'].value);
                const maxSize = Number(state.validateValues['fileSize'].value);
                const allowExtension = state.validateValues['fileExtension'].value;

                let hasUrl = false;
                if (typeof state.content.referenceUrl === 'string') {
                    // 参考URLを改行コードで分ける
                    const parts = String(state.content.referenceUrl).split('\n');
    
                    for (const key in parts) {
                        // 参考URLが記述されているか
                        if (parts[key].length !== 0) {
                            // 参考URLが記述されている場合フラグをtrueにする
                            hasUrl = true;
                            break;
                        }
                    }
                }

                // ファイル数が上限以下であるか確認する
                if (files.length > maxCount) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_FILE_COUNT, [
                        "添付",
                        state.validateValues['fileCount'].value + 'ファイル以下']);
                }

                // ファイルサイズが0バイトより大きく、上限以下であるか確認する
                for (const key in files) {
                    if (files[key].fileSize > maxSize) {
                        // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_MAX_SIZE, [maxSize / 1024]);
                    } else if (files[key].fileSize === 0) {
                        return MessageConst.E_FILE_NO_DATA;
                    }
                }

                // 拡張子が登録対象か確認する
                for (const key in files) {
                    // 登録しようとするファイルの拡張子を得る
                    const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                    const fileExtension = parts[1];

                    // m_parametersの拡張子一覧
                    const validateExtension = allowExtension.split(',');

                    if (validateExtension.indexOf(fileExtension) === -1) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_EXTENSION, [allowExtension]);
                    }
                }

                // ファイル名が重複していないか確認する
                if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_FILE_NAME_DUPLICATE;
                }

                // ファイル、掲載内容、参考URLが未入力なら登録処理不可
                if (files.length === 0 && state.content.bodyText.length === 0 && !hasUrl) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_THREE_OF_ONE, ['掲載内容', '参考URL', '添付ファイル']);
                }

                return true;
            }
        ];

        // リンクURLルール
        const linkUrlRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['リンクURL']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['linkUrl'], 'リンクURL'),
            // 属性チェック（http:// か https:// から始まる）
            (v: any) => v === null || v.startsWith(SystemConst.URL_SCHEME['HTTP']) || v.startsWith(SystemConst.URL_SCHEME['HTTPS'])
                || CommonFunction.replaceArray(MessageConst.E_URL_SCHEME_ERR, [SystemConst.URL_SCHEME['HTTP'], SystemConst.URL_SCHEME['HTTPS']])
        ];

        let contentId = '';                                           // 編集対象のコンテンツID

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.content);
        // カテゴリが選択された場合
        watch(activeNode, () => {
            // 教育用コンテンツ一覧画面に遷移する
            selectCategory();
        })

        /**
         * 初期処理
         */
        onMounted(() => {
            // カテゴリデータデータの取得関数呼び出し
            getCategory();

            init();
        });

        /**
         * カテゴリデータの取得
         */
        async function getCategory() {
            // カテゴリ取得
            categoryRef.value.getCategory();
            state.categoryId = ctx.root.$store.state.content.activeNode[0];
        }

        async function init() {
            // 教育用コンテンツ登録初期表示取得APIで初期表示データを取得する
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('content/getInitParameters');

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            if (!state.isAddMode) {
                // 教育用コンテンツ編集画面の場合
                // 教育用コンテンツ更新/削除APIで初期表示データを取得する
                // パラメータ：コンテンツID
                const response = await ctx.root.$store.dispatch('content/getOperateContent',
                    {
                        contentId: contentId
                    }
                );

                // HTTPステータスコードが200以外の場合
                if (response.status !== HttpStatus.OK) {
                    // HTTPステータスコードが200以外の場合
                    if (response.status === HttpStatus.NOT_FOUND) {
                        // HTTPステータスコードが404の場合
                        // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
                        // 教育用コンテンツ一覧画面へ戻る
                        commonRef.value.displayErrorMessageTransitionDialog(response, '/content/list');
                    } else {
                        // HTTPステータスコードが200, 404以外の場合
                        //errorDisplayTransitionControlメソッドでエラー画面を表示する
                        commonRef.value.errorDisplayTransitionControl(response);
                    }
                    return;
                }

                // 画面項目に初期表示データを設定する
                // タイトル
                state.content.title = response.data.content.title;
                // 公開範囲
                state.content.publishRange = response.data.content.publicationRangeCode;
                // 作成方式
                state.content.createMethod = response.data.content.createMethodCode;

                if (state.content.createMethod === createStyleSet['simple']) {
                    // 作成方式が「簡易登録」の場合
                    // 掲載内容を設定する
                    // リッチテキストの未入力時は空文字扱いなので、nullの場合は空文字を設定する
                    if (!response.data.content.body) {
                        state.content.bodyText = "";
                    } else {
                        state.content.bodyText = response.data.content.bodyText;
                    }
                    state.content.body = response.data.content.body;

                    // 参考URLを設定する
                    state.content.referenceUrl = response.data.content.referenceUrl;

                    state.content.files = response.data.content.files;
                    // 添付ファイルをfile-pondへ表示する
                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    response.data.content.files.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                        pondRef.value.addFile(e.fileName, {
                            type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                            file: {
                                name: e.fileName,       // ファイル名
                                size: e.fileSize        // ファイルサイズ
                            },
                            metadata: {
                                url: `/api/content/${contentId}/${e.fileSequence}`   // ダウンロード時のURL
                            }
                        });
                    });

                    // バックアップ用変数に設定
                    backupReferenceUrl = state.content.referenceUrl;
                    backupBody = state.content.body;
                    backupBodyText = state.content.bodyText;
                    backupFiles = JSON.parse(JSON.stringify(state.content.files)); // 配列のディープコピー
                } else {
                    // 作成方式が「リンクURL登録」の場合
                    // リンクURLを設定する
                    state.content.linkUrl = response.data.content.linkUrl;
                    
                    // バックアップ用変数に設定
                    backupLinkUrl = state.content.linkUrl;
                }
            }
            // 以下は登録・編集共通処理
            // バリデーション値を設定する
            state.validateValues = response.data.parameters;
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // 編集対象のコンテンツID取得
            contentId = ctx.root.$route.params.id;

            // 編集対象のコンテンツIDが格納されている場合
            if (contentId) {
                // isAddModeをfalseに変更する
                state.isAddMode = false;
            }

            if (state.isAddMode) {
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-plus-outline');

                // mainTitleを「教育用コンテンツ登録」に変更
                ctx.root.$store.commit('setMainTitle', '教育用コンテンツ登録');
            } else {
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-check-outline');

                // mainTitleを「教育用コンテンツ編集」に変更
                ctx.root.$store.commit('setMainTitle', '教育用コンテンツ編集');
            }
        }

        /**
         * リッチテキストエディタの入力イベント
         * 
         * @param body 掲載内容(HTML形式)
         * @param bodyText 掲載内容(文字のみ)
         */
        function textChange(body: string, bodyText: string) {
            // 値が変わった時だけstateを更新
            if (state.content.body !== body || state.content.bodyText !== bodyText) {
                state.content.body = body;
                state.content.bodyText = bodyText;
                validateBodyFiles();
            }
        }

        /**
         * 参考URLの入力イベント
         */
        function referenceUrlChange() {
            validateBodyFiles();
        }

        /**
         * 添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateFiles() {
            validateBodyFiles();
        }

        /**
         * 掲載内容、参考URL、添付ファイルのバリデーションチェック処理
         * いずれかが入力必須であるため、同時にチェックする
         */
        function validateBodyFiles() {
            // 掲載内容のバリデーションを行う
            for (let index = 0; index < bodyRules.length; index++) {
                const rule: any = bodyRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.bodyErrorMessage = valid;
                    state.isBodyValid = false;
                    break;
                } else {
                    // エラーなし
                    state.bodyErrorMessage = '';
                    state.isBodyValid = true;
                }
            }

            // 参考URLのバリデーションを行う
            for (let index = 0; index < referenceUrlRules.length; index++) {
                const rule: any = referenceUrlRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.referenceUrlErrorMessage = valid;
                    state.isReferenceUrlValid = false;
                    break;
                } else {
                    // エラーなし
                    state.referenceUrlErrorMessage = '';
                    state.isReferenceUrlValid = true;
                }
            }

            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < filesRules.length; index++) {
                const rule: any = filesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.fileErrorMessage = valid;
                    state.isFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.fileErrorMessage = '';
                    state.isFileValid = true;
                }
            }
        }

        /**
         * リンクURL登録選択時、参考URL・掲載内容・添付ファイルのクリア
         */
        function clearReferenceUrl() {
            // 編集モードの場合
            if (!state.isAddMode) {
                // リンクURL復元
                state.content.linkUrl = backupLinkUrl;
            }

            // 変数初期化
            // 参考URL
            state.content.referenceUrl = '';
            // 掲載内容
            state.content.body = '';
            state.content.bodyText = '';
            // 添付ファイル（nextTickでDOM更新後に実行）
            nextTick(() => {
                pondRef.value.removeFiles();
            });

            // バリデーション初期化
            // 参考URL
            state.referenceUrlErrorMessage = '';
            state.isReferenceUrlValid = true;
            // 掲載内容
            state.bodyErrorMessage = '';
            state.isBodyValid = true;
            // 添付ファイル
            state.fileErrorMessage = '';
            state.isFileValid = true;
        }

        /**
         * 簡易登録選択時、リンクURLのクリア
         */
        function clearLinkUrl() {
            // 編集モードの場合
            if (!state.isAddMode) {
                // リッチテキストエディタの再描画（keyを変更することで再描画）
                state.bodyEditorKey = Date.now();

                // 参考URL・掲載内容・添付ファイル復元
                state.content.referenceUrl = backupReferenceUrl;
                nextTick(() => {
                    state.content.body = backupBody;
                    state.content.bodyText = backupBodyText;
                });
                state.content.files = JSON.parse(JSON.stringify(backupFiles)); // 配列のディープコピー

                // FilePondのファイルを再設定（nextTickでDOM更新後に実行）
                nextTick(() => {
                    pondRef.value.removeFiles();
                    state.content.files.forEach((e: any) => {
                        pondRef.value.addFile(e.fileName, {
                            type: 'local',
                            file: {
                                name: e.fileName,
                                size: e.fileSize
                            },
                            metadata: {
                                url: `/api/content/${contentId}/${e.fileSequence}`
                            }
                        });
                    });
                });
            }

            // 変数初期化
            // リンクURL
            state.content.linkUrl = '';
        }

        /**
         * 登録確認ダイアログ表示
         */
        function displayDialog() {
            // バリデーションを行う
            //   ・タイトルフォームのバリデーション
            // 作成形式が「リンクURL登録」の場合
            //   ・リンクURLのバリデーション
            const isForm = form.value.validate();

            // 作成形式が「簡易登録」の場合
            //   ・掲載内容（リッチテキスト）のバリデーション
            //   ・参考URLのバリデーション
            //   ・添付ファイルのバリデーション
            if (state.content.createMethod === createStyleSet['simple']) {
                validateBodyFiles();
            } else {
                // 作成方式が簡易登録でない場合は、バリデーションチェックフラグをtrueにしておく
                state.isBodyValid = true;
                state.isReferenceUrlValid = true;
                state.isFileValid = true;
            }

            if (!isForm || !state.isBodyValid || !state.isReferenceUrlValid || !state.isFileValid) {
                // バリデーションエラーの際にフォームの一番上にスクロールする
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            let displayMessage = '';
            // 登録処理・編集処理の判定
            if(state.isAddMode) {
                // 登録処理の場合
                // 登録処理実行ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            } else {
                // 編集処理の場合
                // 編集処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['編集']);
            }

            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じ、教育用コンテンツ登録/編集画面へ戻る
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            // フォームデータ作成
            const content = {
                'categoryId': state.categoryId,
                'title': state.content.title,
                'publicationRangeCode': state.content.publishRange,
                'createMethodCode': state.content.createMethod,
                'body': '',
                'bodyText': '',
                'referenceUrl': '',
                'linkUrl': ''
            };

            let files = [];
            let response: any;

            if (state.content.createMethod === createStyleSet['simple']) {
                // 作成方式が簡易登録の場合
                // ・引数の掲載内容を設定
                // ・引数の参考URLを設定
                // ・引数の添付ファイルをFilePondから取得
                content['body'] = state.content.body;
                content['bodyText'] = state.content.bodyText;
                content['referenceUrl'] = state.content.referenceUrl;
                files = pondRef.value.getFiles().map((e: FilePondFile) => e.file);
            } else if (state.content.createMethod === createStyleSet['link']) {
                // 作成方式がリンクURL登録の場合
                // ・引数のURLを設定
                content['linkUrl'] = state.content.linkUrl;
            }

            // 登録処理・編集処理の判断
            if(state.isAddMode) {
                // 登録画面の場合
                // 教育用コンテンツ登録APIでデータを登録する
                // POST /api/content
                response = await ctx.root.$store.dispatch(
                    'content/addContent',
                    {
                        content,
                        files: files
                    }
                );
            } else {
                // 編集画面の場合
                let realFiles: File[] = [];

                // 作成方式が簡易登録の場合
                if(state.content.createMethod === createStyleSet['simple']) {                
                    const files: FilePondFile[] = pondRef.value.getFiles();
                    // content.files（DB上のファイル）が、files（アップロードするファイル）
                    // にないものはcontent.filesから消す
                    const newFiles: ContentFile[] = [];
                    state.content.files.forEach(e => {
                        if (files.filter(x => x.file.name === e.fileName).length !== 0) {
                            newFiles.push(e);
                        }
                    });
                    state.content.files = newFiles;

                    // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
                    // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
                    files.forEach(e => {
                        if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                            if (state.content.files.filter(x => x.fileName === e.file.name).length !== 0) {
                                const r = state.content.files.find(x => x.fileName === e.file.name);
                                if (r) {
                                    // ファイルサイズを更新・ファイルシーケンスを削除
                                    r.fileSize = e.fileSize;
                                    r.fileSequence = undefined;
                                }
                            } else {
                                // DBに無い、新しいファイル
                                state.content.files.push({
                                    'fileName': e.file.name,
                                    'fileSize': e.fileSize
                                });
                            }
                        }
                    });

                    // files（アップロードするファイル）をcontent.filesへ追加する。
                    realFiles = pondRef.value.getFiles().map((e: FilePondFile) => e.file);
                } else {
                    // 作成方式がリンクURL登録の場合
                    // 設定済のファイル情報をリセットする
                    state.content.files = [];
                }

                // 教育用コンテンツ編集APIでデータを編集する
                // POST /api/content/{contentId}
                response = await ctx.root.$store.dispatch(
                    'content/updateContent',
                    {
                        contentId: contentId,
                        content:
                        {
                            'categoryId': content.categoryId,
                            'title': content.title,
                            'publicationRangeCode': content.publicationRangeCode,
                            'createMethodCode': content.createMethodCode,
                            'body': content.body,
                            'bodyText': content.bodyText,
                            'referenceUrl': content.referenceUrl,
                            'linkUrl': content.linkUrl,
                            'files': state.content.files
                        },
                        files: realFiles
                    }
                );
            }

            // 以下は登録・編集共通処理
            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドで教育用コンテンツ一覧画面に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/content/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            commonRef.value.displayMessageTransitionDialog(
                response,
                '/content/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // ストアのページ情報をクリア
            ctx.root.$store.commit('content/clearStorePage');
            // 教育用コンテンツ一覧画面へ遷移
            router.push('/content/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);

            // ウィンドウの幅が狭まってリッチテキストの縦幅が広がった場合はバリデーションメッセージ表示位置を下げる
            if (state.windowSize.x > 1265) {
                state.isToolbarBr = false;
            } else {
                state.isToolbarBr = true;
            }
        }

        return {
            state: readonly(state),
            formHeight,
            form,
            pondRef,
            commonRef,
            categoryRef,
            maxLength,
            titleRules,
            linkUrlRules,
            createStyleSet,
            publishRange,
            textChange,
            referenceUrlChange,
            updateFiles,
            clearReferenceUrl,
            clearLinkUrl,
            displayDialog,
            processingData,
            back,
            onResize
        };
    }
});

